/**
 * useSurfaceGroupsData - Hook to load surface data from Surface groups
 *
 * Retrieves Surface groups from the project state and reconstructs
 * surface plane objects from their saved properties.
 */

import { useCallback } from 'react';
import { GroupType } from '../../types/graphqlTypes';
import { useSurfaceProperties } from '../../modules/Properties/integration/annotations/SurfaceProperties';
import { useAnnotationTree } from './useAnnotationContext';

/** Surface plane data structure */
interface SurfacePlane {
  position: { x: number; y: number; z: number };
  normal: { x: number; y: number; z: number };
}

interface SurfaceGroupData {
  groupIdentifier: string;
  name: string;
  plane: SurfacePlane;
  tolerance?: number;
}

/**
 * Hook to load surface data from Surface groups in the project
 */
export const useSurfaceGroupsData = (project?: { id: string }) => {
  const { getSurfacePlanePositionProperty, getSurfacePlaneNormalProperty, getSurfaceToleranceProperty } = useSurfaceProperties(project?.id || '');
  const annotationContextAnnotations = useAnnotationTree();
  const groups = annotationContextAnnotations.filter((item) => 'annotations' in item) as { type: string; identifier: string; name: string }[];

  /**
   * Load all surfaces from Surface groups in the project
   * Returns an array of SurfaceGroupData objects
   */
  const loadSurfacesFromGroups = useCallback((): SurfaceGroupData[] => {
    // Get all Surface groups from annotation context
    const surfaceGroups = groups.filter((group) => group.type === GroupType.Surface);

    if (surfaceGroups.length === 0) {
      return [];
    }

    const surfaceData: (SurfaceGroupData | null)[] = surfaceGroups.map((group) => {
      const surfacePlanePosition = getSurfacePlanePositionProperty(group.identifier).value;
      const surfacePlaneNormal = getSurfacePlaneNormalProperty(group.identifier).value;
      const surfaceTolerance = getSurfaceToleranceProperty(group.identifier).value;

      if (!surfacePlanePosition || !surfacePlaneNormal) {
        return null;
      }

      return {
        groupIdentifier: group.identifier,
        name: group.name,
        plane: {
          position: {
            x: surfacePlanePosition.x,
            y: surfacePlanePosition.y,
            z: surfacePlanePosition.z,
          },
          normal: {
            x: surfacePlaneNormal.x,
            y: surfacePlaneNormal.y,
            z: surfacePlaneNormal.z,
          },
        },
        tolerance: surfaceTolerance,
      };
    });

    return surfaceData.filter((data): data is SurfaceGroupData => data !== null);
  }, [groups, getSurfacePlanePositionProperty, getSurfacePlaneNormalProperty, getSurfaceToleranceProperty]);

  /**
   * Get a single surface's plane data by group identifier
   */
  const getSurfacePlaneData = useCallback(
    (groupIdentifier: string): SurfacePlane | null => {
      const position = getSurfacePlanePositionProperty(groupIdentifier).value;
      const normal = getSurfacePlaneNormalProperty(groupIdentifier).value;

      if (!position || !normal) {
        return null;
      }

      return {
        position: { x: position.x, y: position.y, z: position.z },
        normal: { x: normal.x, y: normal.y, z: normal.z },
      };
    },
    [getSurfacePlanePositionProperty, getSurfacePlaneNormalProperty],
  );

  return {
    loadSurfacesFromGroups,
    getSurfacePlaneData,
  };
};
